import { useEffect, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { PageHeader } from '@/shared/components/PageHeader';
import { Icon } from '@/shared/components/ui/Icon';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';
import { ThemeColorsSection } from './settings/ThemeColorsSection';
import { GridAlignmentSection } from './settings/GridAlignmentSection';
import { GlobalFormatsSection } from './settings/GlobalFormatsSection';
import { OrganizationSection } from './settings/OrganizationSection';
import { PreferencesSection } from './settings/PreferencesSection';

/**
 * Settings for the professional (numtable.com), on the model of TMS
 * `src/pages/Configuracion.tsx`.
 *
 * Three things carry over from that design and each earns its place:
 *
 *   - **The section list is the single source of truth.** Adding a section is
 *     adding an entry to `SECTIONS`; nothing else changes.
 *   - **The active section travels in `?s=`**, so a link to one is shareable and
 *     the browser's back button behaves the way it does in a native app.
 *   - **On a phone the menu is the screen.** With no `?s=` you get the list;
 *     picking one replaces it. Desktop shows both at once. That is why the menu
 *     highlights the active row only from `md` up — in a mobile list nothing is
 *     "current", it is just a list you tap through.
 *
 * The client-facing equivalent is `src/client/Settings.tsx`, deliberately a
 * different screen: an end client has no organization to configure.
 */

import type { IconName } from '@/shared/components/ui/Icon';

interface Section {
  readonly id: string;
  readonly aliases?: readonly string[];
  readonly titleKey: MessageKey;
  readonly descriptionKey: MessageKey;
  readonly icon: IconName;
  readonly Component: ComponentType;
}

const SECTIONS: readonly Section[] = [
  {
    id: 'preferences',
    titleKey: 'settings.section.preferences.title',
    descriptionKey: 'settings.section.preferences.description',
    icon: 'settings',
    Component: PreferencesSection,
  },
  {
    id: 'organization',
    titleKey: 'settings.section.organization.title',
    descriptionKey: 'settings.section.organization.description',
    icon: 'home',
    Component: OrganizationSection,
  },
  {
    id: 'theme-colors',
    // `grid-colors` is kept as an alias: the section grew from the grid's
    // twelve colours to the whole site's palette, and every link already out
    // there — menus, docs, a tenant's own bookmark — points at the old id.
    aliases: ['grid-colors', 'colores', 'grid'],
    titleKey: 'settings.section.themeColors.title',
    descriptionKey: 'settings.section.themeColors.description',
    icon: 'palette',
    Component: ThemeColorsSection,
  },
  {
    id: 'grid-alignment',
    aliases: ['alineacion'],
    titleKey: 'settings.section.gridAlignment.title',
    descriptionKey: 'settings.section.gridAlignment.description',
    icon: 'alignment',
    Component: GridAlignmentSection,
  },
  {
    id: 'formats',
    titleKey: 'settings.section.formats.title',
    descriptionKey: 'settings.section.formats.description',
    icon: 'sheets',
    Component: GlobalFormatsSection,
  },
];

export function SettingsPage() {
  const { t } = useI18n();
  const brand = useBrand();
  const [params, setParams] = useSearchParams();

  const requested = params.get('s');
  const selected =
    SECTIONS.find(
      (section) => section.id === requested || section.aliases?.includes(requested ?? ''),
    ) ?? null;
  // With no `?s=`, desktop still needs something in the content pane; mobile
  // shows only the menu, which is what `selected === null` drives below.
  const active = selected ?? SECTIONS[0]!;
  const { Component } = active;

  useEffect(() => {
    document.title = `${t('settings.title')} · ${brand.name}`;
  }, [t, brand.name]);

  return (
    <div className="pb-6 sm:pb-8">
      <div className={selected ? 'hidden md:block' : 'block'}>
        <PageHeader title={t('settings.title')} description={t('settings.subtitle')} />
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <nav
          aria-label={t('settings.title')}
          className={`w-full shrink-0 md:block md:w-64 ${selected ? 'hidden' : 'block'}`}
        >
          <ul className="flex flex-col gap-1">
            {SECTIONS.map((section) => {
              const isActive = section.id === active.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => setParams({ s: section.id })}
                    aria-current={selected?.id === section.id ? 'page' : undefined}
                    className={`tap-target flex w-full items-center gap-3 rounded-full border border-transparent px-3 py-2 text-left transition-colors ${
                      isActive
                        ? 'md:border-border md:bg-accent-soft md:font-medium md:text-accent'
                        : 'hover:bg-surface-3'
                    }`}
                  >
                    <Icon name={section.icon} />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-fg">
                        {t(section.titleKey)}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {t(section.descriptionKey)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <section className={`min-w-0 flex-1 ${selected ? 'block' : 'hidden md:block'}`}>
          {/* Back to the menu — mobile only, where the menu was replaced. */}
          <button
            type="button"
            onClick={() => setParams({})}
            className="tap-target mb-3 inline-flex items-center gap-2 text-sm text-muted hover:text-fg md:hidden"
          >
            <Icon name="menu" />
            {t('common.back')}
          </button>

          <h2 className="mb-3 text-lg font-semibold text-fg md:hidden">
            {t(active.titleKey)}
          </h2>

          <Component />
        </section>
      </div>
    </div>
  );
}
